/**
 * @contract
 *   Назначение:  Компактная карточка Trust Score для кабинета ЧОП
 *                Отображает общий индекс доверия и детализацию по 5 слоям
 *   Компонент:  TrustScoreCard (client-side)
 *   KEYWORDS:   TrustScore, Reputation, ESG, Verification, Finance
 *   LINKS:      USES_API(seo_companies.trust_score)
 */

"use client";

import React from "react";
import {
  Shield,
  CheckCircle,
  Star,
  TrendingUp,
  DollarSign,
  Building,
  Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@oxpa/shared/ui/card";
import { Badge } from "@oxpa/shared/ui/badge";
import { cn } from "@oxpa/shared/lib/utils";

// # START_INTERFACE_LayerItem
interface LayerItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  score: number;
  max: number;
  color: string;
}
// # END_INTERFACE_LayerItem

// # START_INTERFACE_TrustScoreCardProps
interface TrustScoreCardProps {
  /** Общий Trust Score 0-100 */
  trustScore: number;
  /** Верификация пройдена (is_claimed + seo_description) */
  verificationDone: boolean;
  /** Количество внутренних отзывов */
  reviewsCount: number;
  /** ESG-рейтинг 0-100 или null */
  esgRating: number | null;
  /** Балл за Foundation (опционально — для детализации) */
  foundationScore?: number;
  /** Максимум за Foundation */
  foundationMax?: number;
  /** Балл за Verification (опционально — для детализации) */
  verificationScore?: number;
  /** Максимум за Verification */
  verificationMax?: number;
  /** Балл за Reputation (опционально — для детализации) */
  reputationScore?: number;
  /** Максимум за Reputation */
  reputationMax?: number;
  /** Балл за Finance (опционально — для детализации) */
  financeScore?: number;
  /** Максимум за Finance */
  financeMax?: number;
  /** Балл за ESG (опционально — для детализации) */
  esgScore?: number;
  /** Максимум за ESG */
  esgMax?: number;
}
// # END_INTERFACE_TrustScoreCardProps

// # START_FUNCTION_getScoreStyle
/**
 * Определение цвета на основе trust score
 * > 70 — зелёный, 40-70 — синий, < 40 — серый
 */
function getScoreStyle(score: number): {
  text: string;
  bar: string;
  badge: "default" | "secondary" | "outline";
} {
  if (score >= 70) {
    return { text: "text-green-400", bar: "bg-green-500", badge: "default" };
  }
  if (score >= 40) {
    return { text: "text-blue-400", bar: "bg-blue-500", badge: "secondary" };
  }
  return { text: "text-[hsl(var(--portal-text-muted))]", bar: "bg-gray-500", badge: "outline" };
}
// # END_FUNCTION_getScoreStyle

// # START_FUNCTION_ScoreBadge
/**
 * Текстовый бейдж категории Trust Score
 */
function ScoreBadge({ score }: { score: number }) {
  let label: string;
  if (score >= 80) label = "Отличный";
  else if (score >= 60) label = "Хороший";
  else if (score >= 40) label = "Средний";
  else if (score >= 20) label = "Низкий";
  else label = "Критический";

  return <Badge variant="outline" className="text-[10px] text-[hsl(var(--portal-text-muted))] border-[hsl(var(--portal-border-hover))]">{label}</Badge>;
}
// # END_FUNCTION_ScoreBadge

// # START_FUNCTION_LayerRow
/**
 * Строка одного слоя Trust Score с иконкой, названием,
 * прогресс-баром и баллами
 */
function LayerRow({ label, icon, score, max, color }: LayerItem) {
  const percent = max > 0 ? Math.min(100, (score / max) * 100) : 0;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          {icon}
          {label}
        </span>
        <span className="text-xs font-semibold tabular-nums">
          {score.toFixed(0)}/{max}
        </span>
      </div>
      <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all duration-500", color)}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
// # END_FUNCTION_LayerRow

// # START_FUNCTION_TrustScoreCard
export function TrustScoreCard({
  trustScore,
  verificationDone,
  reviewsCount,
  esgRating,
  foundationScore,
  foundationMax = 50,
  verificationScore,
  verificationMax = 15,
  reputationScore,
  reputationMax = 5,
  financeScore,
  financeMax = 20,
  esgScore,
  esgMax = 10,
}: TrustScoreCardProps) {
  // Определение стилей на основе общего счёта
  const { text: scoreTextClass, bar: scoreBarClass } = getScoreStyle(trustScore);

  // Собираем слои, которые переданы
  const layers: LayerItem[] = [];

  if (foundationScore !== undefined) {
    layers.push({
      key: "foundation",
      label: "Юридические данные",
      icon: <Building className="w-3.5 h-3.5 text-[hsl(var(--portal-chart-1))]" />,
      score: foundationScore,
      max: foundationMax,
      color: "bg-[hsl(var(--portal-chart-1))]",
    });
  }

  if (esgScore !== undefined || esgRating !== null) {
    const score = esgScore ?? (esgRating !== null ? Math.round(esgRating / 10) : 0);
    layers.push({
      key: "esg",
      label: "ESG / Госконтроль",
      icon: <Shield className="w-3.5 h-3.5 text-[hsl(var(--portal-chart-2))]" />,
      score,
      max: esgMax,
      color: "bg-[hsl(var(--portal-chart-2))]",
    });
  }

  if (verificationScore !== undefined || verificationDone) {
    const score = verificationScore ?? (verificationDone ? verificationMax : 0);
    layers.push({
      key: "verification",
      label: "Верификация",
      icon: <CheckCircle className="w-3.5 h-3.5 text-[hsl(var(--portal-chart-3))]" />,
      score,
      max: verificationMax,
      color: "bg-[hsl(var(--portal-chart-3))]",
    });
  }

  if (reputationScore !== undefined || reviewsCount > 0) {
    const score = reputationScore ?? Math.min(reputationMax, Math.round((reviewsCount / 10) * reputationMax));
    layers.push({
      key: "reputation",
      label: "Репутация",
      icon: <Star className="w-3.5 h-3.5 text-[hsl(var(--portal-warn))]" />,
      score,
      max: reputationMax,
      color: "bg-[hsl(var(--portal-warn))]",
    });
  }

  if (financeScore !== undefined) {
    layers.push({
      key: "finance",
      label: "Финансы",
      icon: <DollarSign className="w-3.5 h-3.5 text-green-400" />,
      score: financeScore,
      max: financeMax,
      color: "bg-green-500",
    });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            Индекс доверия
          </CardTitle>
          <ScoreBadge score={trustScore} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Крупный номер Trust Score */}
        <div className="flex items-baseline gap-2">
          <span className={cn("text-4xl font-black tabular-nums", scoreTextClass)}>
            {trustScore.toFixed(0)}
          </span>
          <span className="text-muted-foreground text-sm">/ 100</span>
        </div>

        {/* Progress bar общий */}
        <div className="w-full h-2.5 bg-secondary rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all duration-500", scoreBarClass)}
            style={{ width: `${Math.min(100, trustScore)}%` }}
          />
        </div>

        {/* Детализация по слоям */}
        {layers.length > 0 && (
          <div className="space-y-3 pt-2 border-t">
            {layers.map((layer) => (
              <LayerRow key={layer.key} label={layer.label} icon={layer.icon} score={layer.score} max={layer.max} color={layer.color} />
            ))}
          </div>
        )}

        {/* Пояснение */}
        <div className="flex items-start gap-1.5 text-[10px] text-muted-foreground">
          <Info className="w-3 h-3 shrink-0 mt-0.5" />
          <span>
            Trust Score рассчитывается на основе юридических данных, верификации,
            репутации, ESG-рейтинга и финансовых показателей.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
// # END_FUNCTION_TrustScoreCard


